import {
  decodeConversationCompactionSystemNoticePayload,
  isCanonicalTimestamp,
  isCanonicalUuid,
} from '@automatick/shared-client-ts';
import {
  RealtimeRenderDisplayKind,
  RealtimeRenderDisplayStageLabel,
  RealtimeRenderEventStage,
  RealtimeRenderEventType,
  RealtimeRenderState,
} from '@automatick/shared-ts';
import {
  decodeWebChatPulseMetadata,
  decodeWebChatPresentations,
  isWebChatRealtimeAudienceScope,
  isWebChatRealtimeRenderSeverity,
  isWebChatRealtimeRenderState,
  isWebChatRenderDisplayKind,
  isWebChatRenderDisplayStageLabel,
  isWebChatRenderEventStage,
  isWebChatRenderEventType,
  isObjectRecord,
  readRecordProperty,
} from './web-chat-contract-normalizers';
import { WebChatCanonicalRenderEvent } from './web-chat.models';

const requiredStringProperties = [
  'streamId',
  'audienceScope',
  'eventType',
  'checkpoint',
  'workspaceId',
  'sourceSurface',
  'stage',
  'state',
  'message',
  'displayKind',
  'displayStageLabel',
  'severity',
  'occurredOn',
] as const;

const nullableStringProperties = [
  'requestedByUserId',
  'sessionId',
  'threadId',
  'workRequestId',
  'correlationId',
] as const;

const allowedProperties = new Set<string>([
  ...requiredStringProperties,
  ...nullableStringProperties,
  'sequence',
  'promoteToFinalMessage',
  'data',
]);

interface RequiredStringValues {
  readonly streamId: string;
  readonly audienceScope: string;
  readonly eventType: string;
  readonly checkpoint: string;
  readonly workspaceId: string;
  readonly sourceSurface: string;
  readonly stage: string;
  readonly state: string;
  readonly message: string;
  readonly displayKind: string;
  readonly displayStageLabel: string;
  readonly severity: string;
  readonly occurredOn: string;
}

interface NullableStringValues {
  readonly requestedByUserId: string | null;
  readonly sessionId: string | null;
  readonly threadId: string | null;
  readonly workRequestId: string | null;
  readonly correlationId: string | null;
}

/**
 * Canonical SignalR ingress for the authoritative RealtimeRenderEvent wire shape.
 * It rejects invalid traffic without repairing, coercing, or synthesizing source data.
 */
export class WebChatRenderEventCodec {
  decode(value: unknown): WebChatCanonicalRenderEvent | null {
    if (!isObjectRecord(value) || this.hasUnknownProperty(value)) {
      return null;
    }

    const requiredStrings = this.readRequiredStrings(value);
    if (
      !requiredStrings ||
      !isWebChatRenderEventType(requiredStrings.eventType) ||
      !isWebChatRenderEventStage(requiredStrings.stage) ||
      !isWebChatRenderDisplayKind(requiredStrings.displayKind) ||
      !isWebChatRenderDisplayStageLabel(requiredStrings.displayStageLabel) ||
      !isWebChatRealtimeAudienceScope(requiredStrings.audienceScope) ||
      !isWebChatRealtimeRenderState(requiredStrings.state) ||
      !isWebChatRealtimeRenderSeverity(requiredStrings.severity)
    ) {
      return null;
    }

    const sequence = readRecordProperty(value, 'sequence');
    const promoteToFinalMessage = readRecordProperty(
      value,
      'promoteToFinalMessage',
    );
    if (
      !isPositiveSafeInteger(sequence) ||
      typeof promoteToFinalMessage !== 'boolean' ||
      requiredStrings.checkpoint !== String(sequence)
    ) {
      return null;
    }

    const nullableStrings = this.readNullableStrings(value);
    if (
      !nullableStrings ||
      !isCanonicalTimestamp(requiredStrings.occurredOn) ||
      !isCanonicalUuid(requiredStrings.workspaceId) ||
      !isNullableUuid(nullableStrings.requestedByUserId) ||
      !isNullableUuid(nullableStrings.sessionId) ||
      !isNullableUuid(nullableStrings.workRequestId) ||
      !isNullableUuid(nullableStrings.correlationId)
    ) {
      return null;
    }

    const data = Object.hasOwn(value, 'data')
      ? readRecordProperty(value, 'data')
      : null;
    const decodedPresentations = decodeWebChatPresentations(data);
    const decodedPulse = decodeWebChatPulseMetadata(data);
    const compactionNotice =
      requiredStrings.eventType ===
      RealtimeRenderEventType.ConversationSystemNoticeCompaction
        ? decodeConversationCompactionSystemNoticePayload(data)
        : null;
    if (
      !decodedPresentations.isValid ||
      !decodedPulse.isValid ||
      decodedPresentations.presentations.some(
        (presentation) =>
          presentation.workspaceId !== requiredStrings.workspaceId,
      ) ||
      !this.hasValidCompactionContract(
        requiredStrings,
        nullableStrings,
        compactionNotice,
      )
    ) {
      return null;
    }

    return {
      streamId: requiredStrings.streamId,
      audienceScope: requiredStrings.audienceScope,
      eventType: requiredStrings.eventType,
      sequence,
      checkpoint: requiredStrings.checkpoint,
      workspaceId: requiredStrings.workspaceId,
      requestedByUserId: nullableStrings.requestedByUserId,
      sessionId: nullableStrings.sessionId,
      threadId: nullableStrings.threadId,
      workRequestId: nullableStrings.workRequestId,
      correlationId: nullableStrings.correlationId,
      sourceSurface: requiredStrings.sourceSurface,
      stage: requiredStrings.stage,
      state: requiredStrings.state,
      message: requiredStrings.message,
      displayKind: requiredStrings.displayKind,
      displayStageLabel: requiredStrings.displayStageLabel,
      promoteToFinalMessage,
      severity: requiredStrings.severity,
      occurredOn: requiredStrings.occurredOn,
      data,
      pulse: decodedPulse.pulse,
      presentations: [...decodedPresentations.presentations],
      compactionNotice,
    };
  }

  private hasUnknownProperty(value: Record<string, unknown>): boolean {
    return Object.keys(value).some(
      (propertyName) => !allowedProperties.has(propertyName),
    );
  }

  private readRequiredStrings(
    value: Record<string, unknown>,
  ): RequiredStringValues | null {
    const streamId = this.readRequiredString(value, 'streamId');
    const audienceScope = this.readRequiredString(value, 'audienceScope');
    const eventType = this.readRequiredString(value, 'eventType');
    const checkpoint = this.readRequiredString(value, 'checkpoint');
    const workspaceId = this.readRequiredString(value, 'workspaceId');
    const sourceSurface = this.readRequiredString(value, 'sourceSurface');
    const stage = this.readRequiredString(value, 'stage');
    const state = this.readRequiredString(value, 'state');
    const message = this.readRequiredString(value, 'message');
    const displayKind = this.readRequiredString(value, 'displayKind');
    const displayStageLabel = this.readRequiredString(
      value,
      'displayStageLabel',
    );
    const severity = this.readRequiredString(value, 'severity');
    const occurredOn = this.readRequiredString(value, 'occurredOn');
    if (
      streamId === null ||
      audienceScope === null ||
      eventType === null ||
      checkpoint === null ||
      workspaceId === null ||
      sourceSurface === null ||
      stage === null ||
      state === null ||
      message === null ||
      displayKind === null ||
      displayStageLabel === null ||
      severity === null ||
      occurredOn === null
    ) {
      return null;
    }

    return {
      streamId,
      audienceScope,
      eventType,
      checkpoint,
      workspaceId,
      sourceSurface,
      stage,
      state,
      message,
      displayKind,
      displayStageLabel,
      severity,
      occurredOn,
    };
  }

  private readNullableStrings(
    value: Record<string, unknown>,
  ): NullableStringValues | null {
    const requestedByUserId = this.readNullableString(
      value,
      'requestedByUserId',
    );
    const sessionId = this.readNullableString(value, 'sessionId');
    const threadId = this.readNullableString(value, 'threadId');
    const workRequestId = this.readNullableString(value, 'workRequestId');
    const correlationId = this.readNullableString(value, 'correlationId');
    if (
      requestedByUserId === undefined ||
      sessionId === undefined ||
      threadId === undefined ||
      workRequestId === undefined ||
      correlationId === undefined
    ) {
      return null;
    }

    return {
      requestedByUserId,
      sessionId,
      threadId,
      workRequestId,
      correlationId,
    };
  }

  private readRequiredString(
    value: Record<string, unknown>,
    propertyName: (typeof requiredStringProperties)[number],
  ): string | null {
    const propertyValue = readRecordProperty(value, propertyName);
    return typeof propertyValue === 'string' &&
      propertyValue.length > 0 &&
      propertyValue === propertyValue.trim()
      ? propertyValue
      : null;
  }

  private readNullableString(
    value: Record<string, unknown>,
    propertyName: (typeof nullableStringProperties)[number],
  ): string | null | undefined {
    if (!Object.hasOwn(value, propertyName)) {
      return undefined;
    }

    const propertyValue = readRecordProperty(value, propertyName);
    if (propertyValue === null) {
      return null;
    }

    return typeof propertyValue === 'string' &&
      propertyValue.length > 0 &&
      propertyValue === propertyValue.trim()
      ? propertyValue
      : undefined;
  }

  private hasValidCompactionContract(
    required: RequiredStringValues,
    nullable: NullableStringValues,
    notice: ReturnType<typeof decodeConversationCompactionSystemNoticePayload>,
  ): boolean {
    const isCompaction =
      required.eventType ===
      RealtimeRenderEventType.ConversationSystemNoticeCompaction;
    const hasExactTuple =
      required.stage === RealtimeRenderEventStage.SystemNotice &&
      required.state === RealtimeRenderState.Completed &&
      required.displayKind === RealtimeRenderDisplayKind.SystemNotice &&
      required.displayStageLabel === RealtimeRenderDisplayStageLabel.System;
    const hasSystemNoticeDiscriminant =
      required.stage === RealtimeRenderEventStage.SystemNotice ||
      required.displayKind === RealtimeRenderDisplayKind.SystemNotice ||
      required.displayStageLabel === RealtimeRenderDisplayStageLabel.System;

    if (!isCompaction) {
      return !hasSystemNoticeDiscriminant && notice === null;
    }

    return (
      hasExactTuple &&
      notice !== null &&
      notice.workspaceId === required.workspaceId &&
      notice.sessionId === nullable.sessionId &&
      notice.userId === nullable.requestedByUserId &&
      notice.correlationId === nullable.correlationId
    );
  }
}

function isPositiveSafeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
}

function isNullableUuid(value: string | null): boolean {
  return value === null || isCanonicalUuid(value);
}
